import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import lazyWithReload from './lib/lazyWithReload';
import ErrorBoundary from './components/ErrorBoundary';
import { canAccess } from './lib/access';
import { ShieldX } from 'lucide-react';
import { OfflineProvider } from './context/OfflineContext';
import { Toaster } from './components/ui/sonner';
import UpdateBanner from './components/UpdateBanner';
import { OfflineModeBanner } from './components/SyncStatusIndicator';

// Pages
import LoginPage from './pages/LoginPage';
import SelectOutletPage from './pages/SelectOutletPage';
import POSPage from './pages/POSPage.jsx';
import DashboardLayout from './pages/DashboardLayout';
import DashboardOverview from './pages/DashboardOverview';
import PlatformAdminPage from './pages/PlatformAdminPage';
import ReportsPage from './pages/ReportsPage';
import BIRReportsPage from './pages/BIRReportsPage';
import AuditLogsPage from './pages/AuditLogsPage';
import KDSPage from './pages/KDSPage';

// Lazy load other dashboard pages
const MenuManagement = lazyWithReload(() => import('./pages/dashboard/MenuManagement'));
const OutletManagement = lazyWithReload(() => import('./pages/dashboard/OutletManagement'));
const GettingStarted = lazyWithReload(() => import('./pages/GettingStarted'));
const UserManagement = lazyWithReload(() => import('./pages/dashboard/UserManagement'));
const Reports = lazyWithReload(() => import('./pages/dashboard/Reports'));
const Inventory = lazyWithReload(() => import('./pages/dashboard/Inventory'));
const Recipes = lazyWithReload(() => import('./pages/dashboard/Recipes'));
const Customers = lazyWithReload(() => import('./pages/dashboard/Customers'));
const Promotions = lazyWithReload(() => import('./pages/dashboard/Promotions'));
const Settings = lazyWithReload(() => import('./pages/dashboard/Settings'));

// Enterprise Pages
const InventoryPage = lazyWithReload(() => import('./pages/backoffice/InventoryPage'));
const CustomersPage = lazyWithReload(() => import('./pages/backoffice/CustomersPage'));
const RecipesPage = lazyWithReload(() => import('./pages/backoffice/RecipesPage'));
const ReportSchedulingPage = lazyWithReload(() => import('./pages/backoffice/ReportSchedulingPage'));
const OutletSettingsPage = lazyWithReload(() => import('./pages/backoffice/OutletSettingsPage'));
const EmployeesPage = lazyWithReload(() => import('./pages/backoffice/EmployeesPage'));
const CashManagementPage = lazyWithReload(() => import('./pages/backoffice/CashManagementPage'));
const AccountingPage = lazyWithReload(() => import('./pages/backoffice/AccountingPage'));
const KitchenStationsPage = lazyWithReload(() => import('./pages/backoffice/KitchenStationsPage'));
const GiftCardsPage = lazyWithReload(() => import('./pages/backoffice/GiftCardsPage'));
const MarketingPage = lazyWithReload(() => import('./pages/backoffice/MarketingPage'));
const ReservationsPage = lazyWithReload(() => import('./pages/backoffice/ReservationsPage'));
const BranchesPage = lazyWithReload(() => import('./pages/backoffice/BranchesPage'));
const ReportsHubPage = lazyWithReload(() => import('./pages/backoffice/ReportsHubPage'));
const UserManualPage = lazyWithReload(() => import('./pages/UserManualPage'));
const CompanySetupWizard = lazyWithReload(() => import('./pages/CompanySetupWizard'));

// Routes that use the operational DARK theme (KDS / selection / platform).
// NOTE: POS is intentionally LIGHT (matches the back-office dashboard).
const DARK_PREFIXES = ['/kds', '/select-outlet', '/admin', '/platform'];

// Toggles the .dark class on <html> so shadcn portals (dialogs, dropdowns) theme correctly.
const ThemeController = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    const isDark = DARK_PREFIXES.some((p) => pathname === p || pathname.startsWith(p + '/') || pathname === p);
    document.documentElement.classList.toggle('dark', isDark);
  }, [pathname]);
  return null;
};

// Access Denied screen (shown instead of redirecting, to avoid loops)
const AccessDenied = () => (
  <div className="h-screen bg-background flex flex-col items-center justify-center gap-4 text-center px-6">
    <ShieldX className="w-12 h-12 text-destructive" />
    <div>
      <h1 className="text-2xl font-bold text-foreground">Access denied</h1>
      <p className="text-muted-foreground mt-1">You do not have permission to view this page.</p>
    </div>
    <a href="/dashboard" className="text-secondary underline">Back to Dashboard</a>
  </div>
);

// First-login gate: owners with incomplete setup are pushed into the wizard,
// which blocks POS / dashboard / other pages until setup is completed.
const SetupGate = () => {
  const { user, isOwner, isPlatformAdmin, setupCompleted } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    if (!user || isPlatformAdmin) return;
    if (setupCompleted === false && isOwner) {
      // First-login owner lands on the Getting Started checklist, and may freely
      // visit the setup task pages to complete each item (they tick off live).
      const allowed = [
        '/setup', '/getting-started', '/login', '/select-outlet',
        '/dashboard/settings', '/dashboard/outlets', '/dashboard/users', '/dashboard/menu',
        '/backoffice/inventory', '/backoffice/outlet-settings', '/backoffice/recipes', '/pos/tables',
      ];
      if (!allowed.some(a => pathname === a || pathname.startsWith(a + '/'))) {
        navigate('/getting-started', { replace: true });
      }
    }
  }, [user, isOwner, isPlatformAdmin, setupCompleted, pathname, navigate]);
  return null;
};

// Protected Route Component
const ProtectedRoute = ({ children, requireOutlet = false, platformOnly = false }) => {
  const { user, loading, currentOutlet, isPlatformAdmin, roleName } = useAuth();
  const { pathname } = useLocation();

  if (loading) {
    return (
      <div className="h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-secondary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (platformOnly && !isPlatformAdmin) return <Navigate to="/select-outlet" replace />;
  if (requireOutlet && !currentOutlet && !isPlatformAdmin) return <Navigate to="/select-outlet" replace />;
  // Role-based URL gating: deny direct navigation to pages the role can't use.
  if (!canAccess(roleName, pathname, isPlatformAdmin)) return <AccessDenied />;
  return children;
};

// Public Route (redirect if logged in)
const PublicRoute = ({ children }) => {
  const { user, isPlatformAdmin } = useAuth();
  if (user) {
    if (isPlatformAdmin) return <Navigate to="/admin" replace />;
    return <Navigate to="/select-outlet" replace />;
  }
  return children;
};

// Loading fallback
const LoadingFallback = () => (
  <div className="flex items-center justify-center h-64">
    <div className="w-8 h-8 border-2 border-secondary border-t-transparent rounded-full animate-spin" />
  </div>
);

const Lazy = (Comp) => (
  <React.Suspense fallback={<LoadingFallback />}>
    <Comp />
  </React.Suspense>
);

function AppRoutes() {
  return (
    <Routes>
      {/* Public */}
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />

      {/* Operational (dark) standalone screens */}
      <Route path="/select-outlet" element={<ProtectedRoute><SelectOutletPage /></ProtectedRoute>} />
      <Route path="/setup" element={<ProtectedRoute>{Lazy(CompanySetupWizard)}</ProtectedRoute>} />
      <Route path="/pos" element={<ProtectedRoute requireOutlet><POSPage /></ProtectedRoute>} />
      <Route path="/pos/tables" element={<ProtectedRoute requireOutlet><POSPage /></ProtectedRoute>} />
      <Route path="/kds" element={<ProtectedRoute requireOutlet><KDSPage /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute platformOnly><PlatformAdminPage /></ProtectedRoute>} />
      <Route path="/platform/admin" element={<ProtectedRoute platformOnly><PlatformAdminPage /></ProtectedRoute>} />

      {/* Outlet management must be reachable even before the first outlet exists,
          otherwise a brand-new tenant/owner is stuck (requireOutlet would bounce
          them to /select-outlet — a catch-22, since this is where outlets are made). */}
      <Route element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
        <Route path="/dashboard/outlets" element={Lazy(OutletManagement)} />
        <Route path="/getting-started" element={Lazy(GettingStarted)} />
      </Route>

      {/* Back Office (light) — all share the DashboardLayout sidebar */}
      <Route element={<ProtectedRoute requireOutlet><DashboardLayout /></ProtectedRoute>}>
        <Route path="/dashboard" element={<DashboardOverview />} />
        <Route path="/manual" element={Lazy(UserManualPage)} />
        <Route path="/dashboard/menu" element={Lazy(MenuManagement)} />
        <Route path="/dashboard/users" element={Lazy(UserManagement)} />
        <Route path="/dashboard/reports" element={Lazy(Reports)} />
        <Route path="/dashboard/inventory" element={Lazy(Inventory)} />
        <Route path="/dashboard/recipes" element={Lazy(Recipes)} />
        <Route path="/dashboard/customers" element={Lazy(Customers)} />
        <Route path="/dashboard/promotions" element={Lazy(Promotions)} />
        <Route path="/dashboard/settings" element={Lazy(Settings)} />

        <Route path="/finance/reports" element={<ReportsPage />} />
        <Route path="/finance/bir-reports" element={<BIRReportsPage />} />
        <Route path="/finance/audit" element={<AuditLogsPage />} />

        <Route path="/backoffice/inventory" element={Lazy(InventoryPage)} />
        <Route path="/backoffice/customers" element={Lazy(CustomersPage)} />
        <Route path="/backoffice/recipes" element={Lazy(RecipesPage)} />
        <Route path="/backoffice/report-scheduling" element={Lazy(ReportSchedulingPage)} />
        <Route path="/backoffice/outlet-settings" element={Lazy(OutletSettingsPage)} />
        <Route path="/backoffice/employees" element={Lazy(EmployeesPage)} />
        <Route path="/backoffice/cash-management" element={Lazy(CashManagementPage)} />
        <Route path="/backoffice/accounting" element={Lazy(AccountingPage)} />
        <Route path="/backoffice/kitchen-stations" element={Lazy(KitchenStationsPage)} />
        <Route path="/backoffice/gift-cards" element={Lazy(GiftCardsPage)} />
        <Route path="/backoffice/marketing" element={Lazy(MarketingPage)} />
        <Route path="/backoffice/reservations" element={Lazy(ReservationsPage)} />
        <Route path="/backoffice/branches" element={Lazy(BranchesPage)} />
        <Route path="/backoffice/reports-hub" element={Lazy(ReportsHubPage)} />
      </Route>

      {/* Default redirect */}
      <Route path="/" element={<Navigate to="/login" replace />} />
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <OfflineProvider>
            <ThemeController />
            <SetupGate />
            <OfflineModeBanner />
            <AppRoutes />
            <Toaster position="top-right" richColors />
            {/* A POS tab stays open for days and never picked up a new
                build on its own. This reloads only while nothing is busy. */}
            <UpdateBanner />
          </OfflineProvider>
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
